import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { from, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  finalize,
  map,
  reduce,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';

import {
  CustomRoleResponse,
  ErrorResponse,
  SuccessResponse,
} from './hub-config-roles-add-drawer.types';
import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { UpdateGroupRoleSuccess } from '@libs/src/groups/groups-store/groups.actions';
import { Group, GroupRole } from '@libs/src/models/group.model';
import { checkEmptySpaces } from '@libs/src/validator-functions/check-empty-spaces';
import { IAppState } from '@main-client/src/app/app.state';
import { currentGroupSource } from '@main-client/src/app/group-view/group-view.helper';
import { parseInput } from '@main-client/src/app/hub-configuration/hub-configuration.helper';

import last from 'lodash-es/last';

const ADD_ROLES = 'add-roles';
const REVIEW = 'review';
const SUCCESS = 'success';
const SUMMARY = 'summary';

type stageNames = 'add_roles' | 'review' | 'summary' | 'success';
type stages =
  | typeof ADD_ROLES
  | typeof REVIEW
  | typeof SUCCESS
  | typeof SUMMARY;

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-hub-config-roles-bulk-add-drawer',
  templateUrl: './hub-config-roles-bulk-add-drawer.component.html',
})
export class HubConfigRolesBulkAddDrawerComponent implements OnInit {
  group: Group;
  isLoading = false;
  invalidOptions: CustomRoleResponse[] = [];
  roles = new FormControl('', [Validators.required, checkEmptySpaces]);
  rolesWithConfigs = this.fb.group({
    roles: this.fb.array([]),
  });
  validOptions: CustomRoleResponse[] = [];
  viewModes: { [key in stageNames]: stages } = {
    add_roles: ADD_ROLES,
    review: REVIEW,
    success: SUCCESS,
    summary: SUMMARY,
  };
  viewMode: stages = ADD_ROLES;
  completedProgress = 0;

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    private readonly fb: FormBuilder,
    private readonly groupsBackend: GroupsBackend,
    private readonly drawerRef: NzDrawerRef,
    private readonly store: Store<IAppState>,
  ) {}

  ngOnInit() {
    currentGroupSource(this.store, this.destroy$)
      .pipe(
        tap((group) => {
          this.group = group;
        }),
        take(1),
      )
      .subscribe();
  }

  getRoleControls() {
    return this.rolesWithConfigs.get('roles') as FormArray;
  }

  createRoleGroup(role: string) {
    return this.fb.group({
      defaults: this.fb.group({
        expert_status: ['approved'],
        inviteable: [true],
        opted_into_matchmaking: [true],
        searchable: [true],
      }),
      name: [role],
    });
  }

  reviewRoles() {
    const roles = parseInput(this.roles.value);
    roles.forEach((role) => {
      this.getRoleControls().push(this.createRoleGroup(role));
    });
    this.viewMode = this.viewModes.review;
  }

  postRoles(role: any): Observable<CustomRoleResponse> {
    return this.groupsBackend.postRoles(this.group, role).pipe(
      map((response: GroupRole[]) => ({ response, role })),
      catchError((response: HttpErrorResponse) => {
        return of({
          role,
          error: getServerError(response),
        });
      }),
      takeUntil(this.destroy$),
    );
  }

  saveRoles() {
    this.isLoading = true;
    from(this.getRoleControls().value)
      .pipe(
        concatMap((role, index: number) => {
          this.completedProgress = index + 1;
          return this.postRoles(role);
        }),
        reduce(
          ({ failedSegments, passedSegments }, node: CustomRoleResponse) => {
            return {
              failedSegments: [
                ...failedSegments,
                ...(node?.error ? [node] : []),
              ],
              passedSegments: [
                ...passedSegments,
                ...(node?.error ? [] : [node]),
              ],
            };
          },
          { failedSegments: [], passedSegments: [] },
        ),
        finalize(() => {
          this.isLoading = false;
          this.completedProgress = 0;
        }),
        tap(
          ({
            failedSegments,
            passedSegments,
          }: {
            failedSegments: ErrorResponse[];
            passedSegments: SuccessResponse[];
          }) => {
            this.validOptions = [...passedSegments];
            this.invalidOptions = [...failedSegments];
            if (0 < passedSegments.length && 0 === failedSegments.length) {
              this.viewMode = this.viewModes.success;
            } else {
              this.viewMode = this.viewModes.summary;
            }
            this.updateStore(last(this.validOptions));
          },
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  updateStore(role: SuccessResponse | undefined) {
    if (!role) {
      return;
    }
    this.store.dispatch(
      UpdateGroupRoleSuccess({
        group: this.group,
        roles: role.response,
      }),
    );
  }

  retry() {
    const toRetry = this.invalidOptions
      .map((invalidRole) => invalidRole.role.name)
      .join('\n');
    this.roles.reset(toRetry);
    this.getRoleControls().clear();
    this.viewMode = this.viewModes.add_roles;
  }

  closeDrawer(): void {
    this.drawerRef.close();
  }

  removeRow(i: number) {
    this.getRoleControls().removeAt(i);
  }
}
